import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  RadioGroup, RadioGroupItem,
} from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHero } from '@/components/shared/PageHero';
import { CONCERN_CATEGORIES } from '@/data/mockData';
import { generateReference, storage } from '@/lib/storage';
import { Check, AlertTriangle, Camera, Copy, ArrowRight, Megaphone, Clipboard } from 'lucide-react';
import { toast } from 'sonner';

const PUROKS = ['Purok 1', 'Purok 2', 'Purok 3', 'Purok 4', 'Purok 5', 'Purok 6', 'Purok 7'];

const ReportConcern = () => {
  const [form, setForm] = useState({
    category: '',
    urgency: 'Normal',
    purok: '',
    location: '',
    description: '',
    name: '',
    contact: '',
    anonymous: false,
    consent: false,
    photoName: '',
  });
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      update('photoName', file.name);
      toast.success(`Photo attached: ${file.name}`);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.category || !form.purok || !form.location.trim() || !form.description.trim()) {
      return toast.error('Please complete all required fields.');
    }
    if (!form.anonymous && (!form.name.trim() || !form.contact.trim())) {
      return toast.error('Please provide your name and contact, or submit anonymously.');
    }
    if (!form.consent) {
      return toast.error('Please agree to the data privacy notice.');
    }
    const ref = generateReference('C');
    const record = {
      id: ref,
      category: form.category,
      urgency: form.urgency,
      purok: form.purok,
      location: form.location.trim(),
      description: form.description.trim(),
      name: form.anonymous ? 'Anonymous' : form.name.trim(),
      contact: form.anonymous ? '' : form.contact.trim(),
      anonymous: form.anonymous,
      consent: form.consent,
      photoName: form.photoName,
      status: 'Submitted',
      date: new Date().toISOString().slice(0, 10),
      submittedAt: new Date().toISOString(),
      timeline: [
        { status: 'Submitted', date: new Date().toISOString(), note: 'Concern received online.' },
      ],
    };
    storage.push('concerns', record);
    setSubmitted(record);
    toast.success('Concern submitted!', { description: `Reference: ${ref}` });
  };

  if (submitted) return <ConcernSuccess record={submitted} />;

  return (
    <div data-testid="report-concern-page">
      <PageHero
        eyebrow="E-Services"
        title="Report a Concern"
        subtitle="Streetlights, drainage, garbage, public order — let us know what needs attention. Your report goes directly to the barangay action team."
        breadcrumbs={[{ label: 'E-Services', to: '/e-services' }, { label: 'Report a Concern' }]}
      />

      <section className="container-app section-padding">
        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <Card className="lg:col-span-2 p-6 sm:p-8 border-0 shadow-md-soft">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-primary" /> Concern Details
                </h2>
                <p className="text-sm text-muted-foreground mt-1">All fields marked with * are required.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select value={form.category} onValueChange={(v) => update('category', v)}>
                    <SelectTrigger data-testid="concern-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {CONCERN_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Urgency *</Label>
                  <RadioGroup value={form.urgency} onValueChange={(v) => update('urgency', v)} className="grid grid-cols-3 gap-2 mt-2">
                    {['Normal', 'Urgent', 'Critical'].map((u) => (
                      <label key={u} className={`cursor-pointer rounded-lg border px-3 py-2 text-center text-sm font-medium transition ${form.urgency === u ? 'border-primary bg-primary/5 text-primary' : 'hover:bg-secondary'}`}>
                        <RadioGroupItem value={u} className="sr-only" data-testid={`concern-urgency-${u.toLowerCase()}`} />
                        {u}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Purok *</Label>
                  <Select value={form.purok} onValueChange={(v) => update('purok', v)}>
                    <SelectTrigger data-testid="concern-purok"><SelectValue placeholder="Select purok" /></SelectTrigger>
                    <SelectContent>
                      {PUROKS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Specific Location *</Label>
                  <Input value={form.location} onChange={(e) => update('location', e.target.value)} placeholder="Street, landmark, or coordinates" data-testid="concern-location" />
                </div>
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder="Describe the concern in detail. Include duration, severity, and how it affects residents."
                  rows={5}
                  data-testid="concern-description"
                />
              </div>

              <div>
                <Label>Photo (optional)</Label>
                <label className="mt-1.5 flex items-center gap-3 rounded-xl border-2 border-dashed border-border hover:border-primary/40 transition px-4 py-4 cursor-pointer">
                  <Camera className="h-5 w-5 text-primary" />
                  <span className="text-sm text-muted-foreground flex-1">{form.photoName || 'Click to attach a photo'}</span>
                  <input type="file" accept="image/*" className="sr-only" onChange={handlePhoto} data-testid="concern-photo" />
                </label>
              </div>

              <div className="rounded-xl border bg-secondary/40 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="cursor-pointer">Submit anonymously</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Your name and contact won't be shared with anyone.</p>
                  </div>
                  <Switch checked={form.anonymous} onCheckedChange={(v) => update('anonymous', v)} data-testid="concern-anonymous" />
                </div>
              </div>

              {!form.anonymous && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Your Name *</Label>
                    <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Full name" data-testid="concern-name" />
                  </div>
                  <div>
                    <Label>Contact Number *</Label>
                    <Input value={form.contact} onChange={(e) => update('contact', e.target.value)} placeholder="0917-XXX-XXXX" data-testid="concern-contact" />
                  </div>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.consent}
                  onCheckedChange={(v) => update('consent', Boolean(v))}
                  data-testid="concern-consent"
                  className="mt-0.5"
                />
                <span className="text-sm text-muted-foreground">
                  I agree that my information will be used only for concern intake, verification, and resolution
                  in accordance with the Data Privacy Act of 2012.
                </span>
              </label>

              <Button type="submit" className="w-full bg-gradient-primary text-primary-foreground" size="lg" data-testid="concern-submit">
                <Check className="mr-2 h-4 w-4" /> Submit Concern Report
              </Button>
            </form>
          </Card>

          <aside className="space-y-4">
            <Card className="p-6 border-0 bg-warning/5 border-warning/20 border">
              <AlertTriangle className="h-6 w-6 text-warning" />
              <h3 className="mt-3 font-heading font-semibold">For emergencies, dial 911</h3>
              <p className="mt-1 text-sm text-muted-foreground">Use this form for non-emergency barangay concerns. For fire, medical, or security emergencies, call <strong>911</strong> or the barangay hotline.</p>
              <Button asChild variant="outline" className="mt-4 w-full" data-testid="concern-emergency-cta">
                <Link to="/emergency">Emergency contacts</Link>
              </Button>
            </Card>
            <Card className="p-6 border-0 bg-card">
              <h3 className="font-heading font-semibold">What happens next?</h3>
              <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><span className="font-bold text-primary">1.</span> You receive a reference number.</li>
                <li className="flex gap-2"><span className="font-bold text-primary">2.</span> Tanod / committee verifies the report.</li>
                <li className="flex gap-2"><span className="font-bold text-primary">3.</span> Action is dispatched to the location.</li>
                <li className="flex gap-2"><span className="font-bold text-primary">4.</span> You can track resolution online anytime.</li>
              </ol>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
};

const ConcernSuccess = ({ record }) => {
  const copy = () => {
    navigator.clipboard.writeText(record.id);
    toast.success('Reference number copied');
  };
  return (
    <div data-testid="concern-success-page">
      <PageHero
        eyebrow="Submitted"
        title="Concern recorded — thank you for reporting!"
        subtitle="Your report goes to the barangay action team. Save your reference number to track progress."
        breadcrumbs={[{ label: 'E-Services', to: '/e-services' }, { label: 'Submitted' }]}
      />
      <section className="container-app -mt-12 relative z-10 max-w-2xl mx-auto">
        <Card className="p-8 border-0 shadow-elegant text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-success/15 flex items-center justify-center">
            <Check className="h-7 w-7 text-success" />
          </div>
          <h3 className="mt-4 font-display text-2xl font-bold">Reference Number</h3>
          <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-secondary px-5 py-3 font-mono text-lg font-bold text-primary-deep" data-testid="concern-success-ref">
            {record.id}
            <Button size="icon" variant="ghost" onClick={copy} className="h-8 w-8" data-testid="concern-success-copy">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-5 text-sm text-muted-foreground">
            Concern recorded under <strong>{record.category}</strong> in <strong>{record.purok}</strong>. {record.urgency !== 'Normal' && `Marked as ${record.urgency}.`}
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button asChild variant="outline" data-testid="concern-success-track">
              <Link to={`/track-request?ref=${record.id}`}>
                <Clipboard className="mr-2 h-4 w-4" /> Track this concern
              </Link>
            </Button>
            <Button asChild className="bg-gradient-primary text-primary-foreground" data-testid="concern-success-home">
              <Link to="/">Back to home <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default ReportConcern;
